import type { PropertyFilterProps } from "@amzn/awsui-components-console";
import {
  FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS,
  FIXED_FEE_CONFIG_ATTRIBUTES,
  LINK_CELL_WRAPPER,
  NUMBER_CELL_WRAPPER,
  STRING_OR_DATE_CELL_WRAPPER,
} from "src/components/allFixedFeeConfigsPage/fixed-fee-config-attributes";
import type { RegionAwareConfig } from "src/config/facd-all-regions";

/**
 * Minimum free-text query length. Matched as a substring against every column,
 * a two or three character value selects most of the table, so a shorter query
 * matches no rows rather than returning noise. Short values (currency codes,
 * marketplace IDs, statuses) stay searchable through their own property.
 */
export const MIN_FREE_TEXT_LENGTH = 6;

const isNonEmpty = (value?: string): value is string => !!value;

/**
 * Values of a list-type column (array or link-array), as displayed in the
 * cell. Returns undefined for columns that are not list-type.
 */
const getListCellValues = (
  attribute: string,
  item: RegionAwareConfig,
): string[] | undefined => {
  const { arrayCell, linkArrayCell } =
    FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS[attribute];

  if (arrayCell) {
    return arrayCell(item);
  }
  if (linkArrayCell) {
    return (linkArrayCell.items(item) ?? [])
      .map((element) => linkArrayCell.label(element))
      .filter(isNonEmpty);
  }
  return undefined;
};

/**
 * Single-value string representation of a scalar column, used for both
 * filtering and the filter's value dropdown. Returns undefined for list-type
 * columns, which carry many values per row.
 */
const getScalarCellValue = (
  attribute: string,
  item: RegionAwareConfig,
): string | undefined => {
  const { stringCell, dateCell, badgeCell, linkCell, numberCell } =
    FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS[attribute];

  if (stringCell) {
    return STRING_OR_DATE_CELL_WRAPPER(stringCell(item));
  }
  if (dateCell) {
    return STRING_OR_DATE_CELL_WRAPPER(dateCell(item));
  }
  if (badgeCell) {
    const badge = badgeCell(item);
    return badge?.uniqueValue ?? badge?.content ?? "";
  }
  if (linkCell) {
    return LINK_CELL_WRAPPER(linkCell(item, item.region));
  }
  if (numberCell) {
    return NUMBER_CELL_WRAPPER(
      numberCell.value(item),
      numberCell.decimalPlaces,
    );
  }
  return undefined;
};

/**
 * Every string of one column that free text should match against. Link-array
 * columns may expose more than they display: Fuse Offers contributes both the
 * offer ID and the contract ID, so either finds the row.
 */
export const getFreeTextStrings = (
  attribute: string,
  item: RegionAwareConfig,
): string[] => {
  const { badgeCell, linkArrayCell } =
    FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS[attribute];

  if (badgeCell) {
    const badge = badgeCell(item);
    return [badge?.content, badge?.uniqueValue].filter(isNonEmpty);
  }
  if (linkArrayCell?.searchValues) {
    const { items, searchValues } = linkArrayCell;
    return (items(item) ?? []).flatMap(searchValues).filter(isNonEmpty);
  }

  const listValues = getListCellValues(attribute, item);
  if (listValues) {
    return listValues.filter(isNonEmpty);
  }
  return [getScalarCellValue(attribute, item)].filter(isNonEmpty);
};

/**
 * Distinct values offered in the filter's value dropdown for one property.
 * Date properties are filtered through a date form instead, so they offer none.
 */
export const getPropertyFilterOptions = (
  propertyKey: string,
  items: RegionAwareConfig[],
): PropertyFilterProps.FilteringOption[] => {
  if (FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS[propertyKey].dateCell) {
    return [];
  }

  const uniqueValues = new Set<string>();
  items.forEach((item) => {
    const values =
      getListCellValues(propertyKey, item) ??
      [getScalarCellValue(propertyKey, item)].filter(isNonEmpty);
    values.forEach((value) => {
      if (value) {
        uniqueValues.add(value);
      }
    });
  });

  return Array.from(uniqueValues)
    .sort()
    .map((value) => ({ propertyKey, value }));
};

/**
 * Matches a free-text token: a case-insensitive substring of any column's
 * searchable strings.
 */
const evaluateFreeTextToken = (
  token: PropertyFilterProps.Token,
  item: RegionAwareConfig,
): boolean => {
  if (token.operator !== ":") {
    console.warn("Unsupported free text operator:", token.operator);
    return false;
  }

  const value = String(token.value);
  if (value.length < MIN_FREE_TEXT_LENGTH) {
    return false;
  }

  const lowerCaseValue = value.toLowerCase();
  return FIXED_FEE_CONFIG_ATTRIBUTES.some((attribute) =>
    getFreeTextStrings(attribute, item).some((searchString) =>
      searchString.toLowerCase().includes(lowerCaseValue),
    ),
  );
};

/** Matches a token scoped to one property. */
const evaluatePropertyToken = (
  token: PropertyFilterProps.Token,
  item: RegionAwareConfig,
): boolean => {
  const propertyKey = token.propertyKey!;
  const attributeConfig = FIXED_FEE_CONFIG_ATTRIBUTE_CONFIGS[propertyKey];
  if (!attributeConfig) {
    console.warn("Unknown filter property:", propertyKey);
    return false;
  }

  const { numberCell } = attributeConfig;
  const value = String(token.value);

  if (numberCell) {
    const numericValue = numberCell.value(item);

    if (numericValue) {
      const numberValue = Number(numericValue);
      const targetNumberValue = Number(value.replace(/,/g, ""));

      switch (token.operator) {
        case "<":
          return numberValue < targetNumberValue;
        case "<=":
          return numberValue <= targetNumberValue;
        case ">":
          return numberValue > targetNumberValue;
        case ">=":
          return numberValue >= targetNumberValue;
        case "=":
        case "!=": {
          // Accept either a raw number ("1000") or the value as the cell and
          // the filter's value dropdown display it ("1,000", or an fxRate
          // rounded to its displayed precision, which no longer compares equal
          // numerically).
          const equals =
            (!Number.isNaN(targetNumberValue) &&
              numberValue === targetNumberValue) ||
            NUMBER_CELL_WRAPPER(numericValue, numberCell.decimalPlaces) ===
              value;
          return token.operator === "=" ? equals : !equals;
        }
      }
    } else if (token.operator !== "=" && token.operator !== "!=") {
      // A row with no value can't be ordered against the target, but it is
      // equally "not equal" to it: let "=" and "!=" fall through to the scalar
      // comparison, where a missing value reads as the empty string.
      return false;
    }
  }

  const scalarValue = getScalarCellValue(propertyKey, item);
  if (scalarValue !== undefined) {
    switch (token.operator) {
      case "=":
        return scalarValue === value;
      case "!=":
        return scalarValue !== value;
      case "<":
        return scalarValue < value;
      case "<=":
        return scalarValue <= value;
      case ">":
        return scalarValue > value;
      case ">=":
        return scalarValue >= value;
      case ":":
        return scalarValue.includes(value);
      case "!:":
        return !scalarValue.includes(value);
    }
    return false;
  }

  // List columns match on whole entries, not substrings.
  const listValues = getListCellValues(propertyKey, item);
  if (listValues) {
    const found = listValues.includes(value);
    if (token.operator === ":") return found;
    if (token.operator === "!:") return !found;
  }

  return false;
};

/** Matches one filter token, free text or property scoped, against a row. */
export const evaluateTokenOnItem = (
  token: PropertyFilterProps.Token,
  item: RegionAwareConfig,
): boolean =>
  token.propertyKey
    ? evaluatePropertyToken(token, item)
    : evaluateFreeTextToken(token, item);

/** Matches a whole filter query against a row, honoring its and/or operation. */
export const evaluateQueryOnItem = (
  query: PropertyFilterProps.Query,
  item: RegionAwareConfig,
): boolean => {
  if (query.tokens.length === 0) {
    return true;
  }

  return query.operation === "or"
    ? query.tokens.some((token) => evaluateTokenOnItem(token, item))
    : query.tokens.every((token) => evaluateTokenOnItem(token, item));
};
